import { NamespaceCache } from './namespace_cache.js'
import { getSelector } from './selector.js'
import type {
  LookupResult,
  NamespaceClientConfig,
  SelectorFunction,
  WorkerInstance,
} from './types.js'

interface ReverseResult {
  public_key: string
  name: string
  found: boolean
}

function createNamespaceError(
  code: string,
  message: string,
  extra: Record<string, unknown> = {},
): Error {
  const err = new Error(message) as Error & Record<string, unknown>
  err.code = code
  Object.assign(err, extra)
  return err
}

/**
 * Namespace client - simple public key resolver
 */
class NamespaceClient {
  namespaceUrl: string
  enableCache: boolean
  cache: NamespaceCache | null
  selector: SelectorFunction

  constructor(config: NamespaceClientConfig) {
    this.namespaceUrl = config.namespaceUrl
    this.enableCache = config.enableCache !== false
    this.cache = this.enableCache ? new NamespaceCache(config.cacheTTL || 300000) : null
    this.selector = getSelector(config.selector)
  }

  async getPublicKey(name: string): Promise<string> {
    const result = await this._lookup(name)

    if (!result.found) {
      throw createNamespaceError('WORKER_NOT_FOUND', `Worker not found: ${name}`, { name })
    }

    if (!result.workers || result.workers.length === 0) {
      throw createNamespaceError('NO_INSTANCES', `No worker instances available: ${name}`, {
        name,
      })
    }

    const worker = this.selector(result.workers, name)
    return worker.public_key
  }

  async getWorkerName(publicKey: string): Promise<string | null> {
    // Check reverse cache first
    const cacheKey = `reverse:${publicKey}`
    if (this.enableCache && this.cache) {
      const cached = this.cache.get(cacheKey)
      if (cached) {
        return cached.name || null
      }
    }

    const result = await this._reverseLookup(publicKey)

    // Handle null/undefined result (e.g., empty results array from server)
    if (!result) return null

    if (this.enableCache && this.cache) {
      // Reverse cache reuses LookupResult shape (workers field unused here)
      this.cache.set(cacheKey, {
        name: result.name,
        workers: [] as WorkerInstance[],
        found: result.found,
      })
    }

    return result.found ? result.name : null
  }

  async _reverseLookup(publicKey: string): Promise<ReverseResult> {
    const results = await this._fetchReverseBatch([publicKey])
    return results[0]
  }

  async _fetchReverseBatch(publicKeys: string[]): Promise<ReverseResult[]> {
    const url = `${this.namespaceUrl}/api/v1/namespace/reverse`

    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_keys: publicKeys }),
      })
    } catch (err: any) {
      throw createNamespaceError(
        'NETWORK_ERROR',
        `Failed to fetch reverse lookup: ${err.message}`,
        { cause: err },
      )
    }

    if (!response.ok) {
      throw createNamespaceError(
        'HTTP_ERROR',
        `Namespace service returned ${response.status}: ${response.statusText}`,
      )
    }

    const data: any = await response.json()

    if (data.code !== 0) {
      throw createNamespaceError(
        data.code.toString(),
        data.msg || 'Namespace service returned error',
      )
    }

    return data.data.results
  }

  async _lookup(name: string): Promise<LookupResult> {
    if (this.enableCache && this.cache) {
      const cached = this.cache.get(name)
      if (cached) {
        return { name: cached.name, workers: cached.workers, found: cached.found }
      }
    }

    const results = await this._fetchBatch([name])
    const result = results[0]

    if (this.enableCache && this.cache) {
      this.cache.set(name, result)
    }

    return result
  }

  async _fetchBatch(names: string[]): Promise<LookupResult[]> {
    const url = `${this.namespaceUrl}/api/v1/namespace/list`

    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ names }),
      })
    } catch (err: any) {
      throw createNamespaceError('NETWORK_ERROR', `Failed to fetch namespace: ${err.message}`, {
        cause: err,
      })
    }

    if (!response.ok) {
      throw createNamespaceError(
        'HTTP_ERROR',
        `Namespace service returned ${response.status}: ${response.statusText}`,
      )
    }

    const data: any = await response.json()

    if (data.code !== 0) {
      throw createNamespaceError(
        data.code.toString(),
        data.msg || 'Namespace service returned error',
      )
    }

    return data.data.results
  }
}

export interface NamespaceClientAPI {
  getPublicKey(name: string): Promise<string>
  getWorkerName(publicKey: string): Promise<string | null>
}

/**
 * Create a new namespace client
 */
export function newNamespaceClient(config: NamespaceClientConfig): NamespaceClientAPI {
  const client = new NamespaceClient(config)
  return {
    getPublicKey: client.getPublicKey.bind(client),
    getWorkerName: client.getWorkerName.bind(client),
  }
}
